import {Injectable} from '@angular/core';
import {AuthSessionApiService} from '@npck/auth-frontend-api';
import {OauthRedirectErrorCode} from '@npck/errors-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {ERROR_PARAM, RouterService, TracingService} from '@npck/libs/commons';
import {catchError, Observable, of, timeout} from 'rxjs';
import {AUTH_ERROR} from '../infra/analytics-events';
import {Error} from '../infra/app-error-handler.service';
import {Path} from '../path';
import {AuthHolderService} from './auth-holder.service';
import {TEMP_REDIRECT_URL} from './constants';

const CONSENT_DECISION_TIMEOUT_MS = 2000;

const EXPECTED_ERROR_CODES = [
    OauthRedirectErrorCode.access_denied,
    OauthRedirectErrorCode.auth_iin_step_cancelled,
    OauthRedirectErrorCode.auth_sms_code_cancelled,
    OauthRedirectErrorCode.auth_liveness_step_cancelled,
    OauthRedirectErrorCode.auth_bin_step_cancelled,
    OauthRedirectErrorCode.esign_is_blocked_and_ready_to_reset,
    OauthRedirectErrorCode.device_error
];

@Injectable({
    providedIn: 'root',
})
export class OauthService {

    constructor(
        private routerService: RouterService,
        private authHolderService: AuthHolderService,
        private analyticsService: AnalyticsService,
        private tracingService: TracingService,
        private authSessionApiService: AuthSessionApiService,
    ) {
    }

    handleOauthErrorAndRedirect(errorCode: OauthRedirectErrorCode, requestId?: string, errorDescription?: string): void {
        this.cancelSession(errorCode).pipe(
            catchError(() => of(null)),
        ).subscribe(() => {
            if (EXPECTED_ERROR_CODES.includes(errorCode)) {
                this.analyticsService.finishSession();
                this.redirectWithError(errorCode, requestId);
            } else {
                this.handleOauthError(errorCode, requestId, errorDescription);
            }
        });
    }

    handleOauthError(errorCode: OauthRedirectErrorCode | Error, requestId?: string, errorDescription?: string): void {
        this.analyticsService.capture(AUTH_ERROR, {reason: errorCode, message: errorDescription});
        this.analyticsService.finishSession();
        this.routerService.navigate(Path.ERROR_REDIRECT, {[ERROR_PARAM]: errorCode, requestId});
    }

    redirect(redirectUri: string, code: string, state: string): void {
        const url = new URL(redirectUri);
        url.searchParams.append('code', code);
        url.searchParams.append('state', state);
        this.tracingService.addNewSpan(url);
        window.location.replace(url);
    }

    redirectWithError(errorCode: OauthRedirectErrorCode, requestId?: string): void {
        let url: URL;
        if (this.authHolderService.authSessionData != null) {
            url = new URL(this.authHolderService.authSessionData.redirectUri);
            url.searchParams.append('state', this.authHolderService.authSessionData.state);
            url.searchParams.append('errorCode', errorCode);
            if (requestId) {
                url.searchParams.append('requestId', requestId);
            }
        } else {
            url = new URL(TEMP_REDIRECT_URL);
        }
        this.tracingService.addNewSpan(url, span => {
                span.setAttribute('error', true);
                span.setAttribute('errorData', errorCode);
                span.setAttribute('requestId', requestId);
            },
        );
        window.location.replace(url);
    }

    reportConsentAccepted(): Observable<unknown> {
        return this.reportConsentDecision(true);
    }

    reportConsentRejected(): Observable<unknown> {
        return this.reportConsentDecision(false);
    }

    private reportConsentDecision(approved: boolean): Observable<unknown> {
        if (!this.authHolderService.authSessionToken) {
            return of(null);
        }
        return this.authSessionApiService.authSessionConsentDecision({approved}).pipe(
            timeout(CONSENT_DECISION_TIMEOUT_MS),
            catchError(() => of(null)),
        );
    }

    private cancelSession(errorCode: OauthRedirectErrorCode): Observable<void> {
        if (!this.authHolderService.hasAuthSession()) {
            return of(null);
        }
        return this.authSessionApiService.authSessionCancel({error: errorCode});
    }
}
